import { __esDecorate, __runInitializers } from "tslib";
import { WebCamRuntime } from "./webCamRuntime.js";
import { ConnectionPointType, InputBlock, createStrongRef, editableInPropertyPage, PropertyTypeForEdition, Logger, } from "smart-filters";
import { Observable } from "core/Misc/observable.js";
import { WebCamInputBlockName } from "../blockNames.js";
const LocalStorageWebcamIdKey = "webCamDeviceId";
const LocalStorageNameKey = "webCamName";
const DefaultWebCamSource = {
    label: "Default",
    value: "",
};
/**
 * A utility class to manage the webcam sources for the WebCamInputBlock
 */
class WebcamSourceManager {
    /**
     * Creates a new WebcamSourceManager
     */
    constructor() {
        /**
         * The list of available webcam sources
         */
        this.sources = [];
        /**
         * An observable that notifies when the sources have been loaded
         */
        this.onSourcesLoaded = new Observable(undefined, true);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._updateWebcamSourcesAsync();
        navigator.mediaDevices?.addEventListener("devicechange", () => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._updateWebcamSourcesAsync();
        });
    }
    async _updateWebcamSourcesAsync() {
        if (!navigator.mediaDevices) {
            Logger.Warn("Webcam not supported by this browser");
            return;
        }
        let foundDefault = false;
        const devices = await navigator.mediaDevices.enumerateDevices();
        const sources = devices
            .filter((device) => device.kind === "videoinput")
            .map((device) => {
            if (device.deviceId === "") {
                foundDefault = true;
                return DefaultWebCamSource;
            }
            return {
                label: device.label,
                value: device.deviceId,
            };
        });
        if (!foundDefault) {
            sources.unshift(DefaultWebCamSource);
        }
        // Only update if the sources have changed (sometimes, they don't)
        if (JSON.stringify(this.sources) !== JSON.stringify(sources)) {
            this.sources = sources;
            this.onSourcesLoaded.notifyObservers(this.sources);
        }
    }
}
/**
 * A special Texture InputBlock which is only for use in the Editor
 * as a convenient way to select a webcam source. In a real application, this should not be used.
 * Instead, that hosting application should manage the webcam itself and provide the texture to the Smart Filter
 * via a regular Texture InputBlock.
 */
let WebCamInputBlock = (() => {
    var _a;
    let _classSuper = InputBlock;
    let _instanceExtraInitializers = [];
    let _set_webcamSourceId_decorators;
    return _a = class WebCamInputBlock extends _classSuper {
            /**
             * The source of the webcam
             */
            get webcamSource() {
                return this._webCamSource;
            }
            /**
             * The source of the webcam
             */
            set webcamSource(webcamSource) {
                if (this._webCamRuntime) {
                    this._webCamRuntime.deviceId = webcamSource.value;
                }
                this._webCamSource = webcamSource;
                // Save the last used webcam source
                localStorage.setItem(LocalStorageWebcamIdKey, webcamSource.value);
                localStorage.setItem(LocalStorageNameKey, webcamSource.label);
            }
            /**
             * The source ID of the webcam
             */
            set webcamSourceId(id) {
                const source = _a._WebCamSourceManager.sources.find((source) => source.value === id);
                this.webcamSource = source ?? this.webcamSource;
            }
            /**
             * The source ID of the webcam
             */
            get webcamSourceId() {
                return this._webCamSource.value;
            }
            /**
             * Creates a new WebCamInputBlock
             * @param smartFilter - the smart filter
             */
            constructor(smartFilter) {
                super(smartFilter, WebCamInputBlockName, ConnectionPointType.Texture, createStrongRef(null));
                this._webCamRuntime = __runInitializers(this, _instanceExtraInitializers);
                // Load the last used webcam source
                const lastWebCamId = localStorage.getItem(LocalStorageWebcamIdKey);
                const lastWebCamName = localStorage.getItem(LocalStorageNameKey);
                if (lastWebCamId && lastWebCamName) {
                    this._webCamSource = {
                        label: lastWebCamName,
                        value: lastWebCamId,
                    };
                }
                else {
                    this._webCamSource = DefaultWebCamSource;
                }
            }
            /**
             * Initializes the webcam runtime
             * @param engine - the engine to use
             * @returns the initialized WebCamRuntime
             */
            initializeWebCamRuntime(engine) {
                if (this._webCamRuntime) {
                    this._webCamRuntime.dispose();
                }
                this._webCamRuntime = new WebCamRuntime(engine, this.runtimeValue);
                this._webCamRuntime.deviceId = this._webCamSource.value;
                return this._webCamRuntime;
            }
        },
        (() => {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
            _set_webcamSourceId_decorators = [editableInPropertyPage("Source", PropertyTypeForEdition.List, "PROPERTIES", {
                    notifiers: { update: true },
                    get options() {
                        return WebCamInputBlock._WebCamSourceManager.onSourcesLoaded;
                    },
                    valuesAreStrings: true,
                })];
            __esDecorate(_a, null, _set_webcamSourceId_decorators, { kind: "setter", name: "webcamSourceId", static: false, private: false, access: { has: obj => "webcamSourceId" in obj, set: (obj, value) => { obj.webcamSourceId = value; } }, metadata: _metadata }, null, _instanceExtraInitializers);
            if (_metadata) Object.defineProperty(_a, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        })(),
        _a._WebCamSourceManager = new WebcamSourceManager(),
        _a;
})();
export { WebCamInputBlock };
//# sourceMappingURL=webCamInputBlock.js.map